import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GTop100Logo from '../assets/GTop100Logo';
import api from '../utils/api';
import './Dashboard.css';

const NX_REWARD_PER_VOTE = 2000;

const Dashboard = () => {
  const [userInfo, setUserInfo] = useState({
    username: localStorage.getItem('username') || 'User',
    meso: 0,
    characters: [],
    banned: false,
    banReason: '',
    votePoints: 0,
    nxCredit: 0,
    maplePoint: 0,
    nxPrepaid: 0,
    todayVotes: 0,
    canVoteToday: false
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [voteLoading, setVoteLoading] = useState(false);
  const [voteMessage, setVoteMessage] = useState(null);
  const navigate = useNavigate();
  
  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/auth/user');
      
      setUserInfo({
        id: res.data.id,
        username: res.data.username,
        email: res.data.email,
        banned: res.data.banned,
        banReason: res.data.banReason,
        nxCredit: res.data.nxCredit,
        maplePoint: res.data.maplePoint,
        nxPrepaid: res.data.nxPrepaid,
        votePoints: res.data.votePoints,
        totalMeso: res.data.totalMeso,
        characters: res.data.characters || [],
        todayVotes: 0,
        canVoteToday: true
      });
      
      await fetchVotingStatus();
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      if (err.response && err.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchVotingStatus = async () => {
    try {
      const res = await api.get('/voting/status');
      
      setUserInfo(prevState => ({
        ...prevState,
        votePoints: res.data.votePoints,
        todayVotes: res.data.todayVotes,
        canVoteToday: res.data.canVoteToday
      }));
      
    } catch (err) {
      console.error('Error fetching voting status:', err);
    }
  };
  
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const token = localStorage.getItem('token');
    
    if (!isAuthenticated || !token) {
      navigate('/login');
      return;
    }
    
    fetchUserData();
  }, [navigate]);
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    navigate('/login');
  };
  
  const handleVote = async () => {
    const userId = userInfo.id;
    
    // Updated voting URL using pingUsername parameter
    const voteUrl = `https://gtop100.com/MapleStory/Kuro-a-revolutionary-MapleStory-private-server-entering-its-Alpha-stage-soon-104650?vote=1&pingUsername=${userId}`;
    
    window.open(voteUrl, '_blank');
    
    setVoteLoading(true);
    setVoteMessage({
      type: 'info',
      text: 'Vote page opened. After voting, wait a moment for confirmation...'
    });
    
    let pollAttempts = 0;
    const maxAttempts = 5;
    
    const pollInterval = setInterval(async () => {
      try {
        pollAttempts++;
        console.log(`Polling for vote status (attempt ${pollAttempts}/${maxAttempts})...`);
        
        const res = await api.get('/voting/status');
        
        if (res.data.todayVotes > userInfo.todayVotes) {
          clearInterval(pollInterval);
          
          setUserInfo(prevState => ({
            ...prevState,
            votePoints: res.data.votePoints,
            todayVotes: res.data.todayVotes,
            canVoteToday: res.data.canVoteToday
          }));
          
          setVoteMessage({
            type: 'success',
            text: `Vote successful! You earned ${NX_REWARD_PER_VOTE} NX.`
          });
          
          setVoteLoading(false);
        } else if (pollAttempts >= maxAttempts) {
          clearInterval(pollInterval);
          
          try {
            console.log('Trying direct verification as backup...');
            const verifyRes = await api.post('/voting/verify');
            
            setUserInfo(prevState => ({
              ...prevState,
              votePoints: verifyRes.data.votePoints,
              todayVotes: verifyRes.data.todayVotes,
              nxCredit: verifyRes.data.nxCredit,
              canVoteToday: verifyRes.data.todayVotes < 3
            }));
            
            setVoteMessage({
              type: 'success',
              text: verifyRes.data.msg
            });
          } catch (verifyErr) {
            console.error('Error with direct vote verification:', verifyErr);
            setVoteMessage({
              type: 'warning',
              text: 'Vote may have been processed. Please refresh the page to check your balance.'
            });
          } finally {
            setVoteLoading(false);
          }
        }
      } catch (err) {
        console.error('Error checking vote status:', err);
        if (pollAttempts >= maxAttempts) {
          clearInterval(pollInterval);
          setVoteMessage({
            type: 'error',
            text: 'Error verifying vote. Please check if your vote registered.'
          });
          setVoteLoading(false);
        }
      }
    }, 5000);
  };
  
  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading account information...</p>
      </div>
    );
  }
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Account Dashboard</h1>
        <button className="logout-button" onClick={handleLogout}>Logout</button>
      </div>
      
      {userInfo.banned && (
        <div className="ban-notice">
          <h3>Account Banned</h3>
          <p>Reason: {userInfo.banReason || 'Violation of server rules'}</p>
          <p>Please contact an administrator on Discord for assistance.</p>
        </div>
      )}
      
      <div className="dashboard-content">
        <div className="dashboard-section account-info">
          <h2>Account Information</h2>
          <div className="info-row">
            <span className="info-label">Username:</span>
            <span className="info-value">{userInfo.username}</span>
          </div>
          <div className="info-row">
            <span className="info-label">NX Credit:</span>
            <span className="info-value">{userInfo.nxCredit.toLocaleString()}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Vote Points:</span>
            <span className="info-value">{userInfo.votePoints}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Total Meso:</span>
            <span className="info-value">{userInfo.totalMeso?.toLocaleString() || '0'}</span>
          </div>
        </div>
        
        <div className="dashboard-section characters">
          <h2>Characters</h2>
          {userInfo.characters.length === 0 ? (
            <p className="no-characters">No characters found on this account.</p>
          ) : (
            <div className="character-list">
              {userInfo.characters.map(character => (
                <div key={character.id} className="character-card">
                  <h3 className="character-name">{character.name}</h3>
                  <div className="character-detail">
                    <span className="detail-label">Level:</span>
                    <span className="detail-value">{character.level}</span>
                  </div>
                  <div className="character-detail">
                    <span className="detail-label">Job:</span>
                    <span className="detail-value">{character.job}</span>
                  </div>
                  <div className="character-detail">
                    <span className="detail-label">Meso:</span>
                    <span className="detail-value">{character.meso?.toLocaleString() || '0'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="dashboard-section voting">
          <h2>Server Voting</h2>
          <div className="gtop-logo-container">
            <GTop100Logo />
          </div>
          <p>Vote for Kuro on GTop100 to receive 2000 NX per vote!</p>
          <p>You can vote up to 3 times per day.</p>
          
          {voteMessage && (
            <div className={`vote-message ${voteMessage.type}`}>
              {voteMessage.text}
            </div>
          )}
          
          <div className="vote-stats">
            <div className="vote-stat">
              <span className="stat-label">Today's Votes:</span>
              <span className="stat-value">{userInfo.todayVotes}/3</span>
            </div>
            <div className="vote-stat">
              <span className="stat-label">Total Vote Points:</span>
              <span className="stat-value">{userInfo.votePoints}</span>
            </div>
            <div className="vote-stat">
              <span className="stat-label">NX Earned Today:</span>
              <span className="stat-value">{userInfo.todayVotes * 2000}</span>
            </div>
          </div>
          
          <button 
            className={`vote-button ${voteLoading ? 'loading' : ''} ${!userInfo.canVoteToday ? 'disabled' : ''}`} 
            onClick={handleVote}
            disabled={voteLoading || !userInfo.canVoteToday}
          >
            {voteLoading ? 'Processing...' : userInfo.canVoteToday ? 'Vote Now' : 'Daily Limit Reached'}
          </button>
          
          {!userInfo.canVoteToday && (
            <p className="vote-limit-message">
              You've reached your daily voting limit (3/3). Come back tomorrow!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;